/* eslint-disable quotes */
/* eslint-disable max-len */
import { observer } from 'mobx-react';
import { useStore } from '../../../core/utils/hook';
import moment from 'moment';

import './styles.scss';
import { flowResult } from 'mobx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getMasterCompanyIds } from '../../../core/utils/gaFilter';

import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import iconv from 'iconv-lite';
import { useNavigate } from 'react-router-dom';

export const DonutChart = ({ percent = 0, size = 150, thickness = 0.66 }) => {
    const safePercent = Math.min(100, Math.max(0, percent));

    const donutStyle = {
        width: `${size}px`,
        height: `${size}px`,
        background: `conic-gradient(#FFA600 0% ${safePercent}%, #FFF3D9 ${safePercent}% 100%)`,
        borderRadius: '50%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const innerSize = size * thickness;

    return (
        <div className="donut-chart" style={donutStyle}>
            <div
                className="donut-hole"
                style={{
                    width: `${innerSize}px`,
                    height: `${innerSize}px`,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    zIndex: 1
                }}
            ></div>
            <div
                className="donut-center"
                style={{
                    fontSize: `${size * 0.2}px`,
                    fontWeight: 'bold',
                    color: '#FFA600',
                    zIndex: 2
                }}
            >
                {safePercent}%
            </div>
        </div>
    );
};

const FirstTest = observer(() => {
    const navigate = useNavigate();
    const [averageScore, setAverageScore] = useState(0);
    const [courseList, setCourseList] = useState({});
    const [eiScore, setEiScore] = useState(0);
    const [activeTab, setActiveTab] = useState('average');

    // store
    const {
        userCourseStore: { getAverageScore, getCoursesAndAverageScore, getDataCSV },
        groupAdminStore: { selectedGroupIds, selectedDepartmentIds, gaFilterMode, childrenOfGroup },
        userStore: { userInfoHeader }
    } = useStore();

    const isDepartmentFilter = useMemo(() => {
        if (userInfoHeader?.departmentId) {
            return true;
        }
        return !!userInfoHeader?.enableDepartmentHierarchy && gaFilterMode === 'department';
    }, [userInfoHeader?.departmentId, userInfoHeader?.enableDepartmentHierarchy, gaFilterMode]);

    const buildFirstTestPayload = (extraPayload = {}) => {
        if (isDepartmentFilter) {
            return {
                ...extraPayload,
                departmentIds: selectedDepartmentIds.join(',')
            };
        }
        return {
            ...extraPayload,
            groupId: selectedGroupIds.join(',')
        };
    };

    // #17989 - sequence guards (same idea as attendancesOfCompanySeq in GroupAdminStore): when the
    // filter changes twice quickly, a late response from the previous scope must not overwrite state.
    const averageScoreSeq = useRef(0);
    const courseListSeq = useRef(0);

    const fetchDataAverageScore = async (payload) => {
        averageScoreSeq.current += 1;
        const seq = averageScoreSeq.current;
        const resAverage = await flowResult(getAverageScore(payload));
        if (seq !== averageScoreSeq.current) return;
        setAverageScore(resAverage ? +resAverage.averageScore.toFixed(1) : 0);
        setEiScore(resAverage ? Number(resAverage.eiScore)?.toFixed(1) || 0 : 0);
    };

    const fetchDataCourseList = async (payload) => {
        courseListSeq.current += 1;
        const seq = courseListSeq.current;
        const resCourseList = await flowResult(getCoursesAndAverageScore(payload));
        if (seq !== courseListSeq.current) return;
        setCourseList(resCourseList || {});
    };

    // #17989 - derive the master company ids here (shared helper with Header) instead of reading the
    // store value the header writes in a *later* effect: with the store value this component fired
    // average-score once with an empty masterCompanyIds, then again when it was filled, and the
    // reference-keyed effects re-ran on every header refresh (3-4 requests of ~2 s each per login).
    // Keying on the serialized payloads makes both requests fire exactly once per real change.
    const masterCompanyIds = useMemo(
        () => getMasterCompanyIds(childrenOfGroup, selectedGroupIds).join(','),
        [childrenOfGroup, selectedGroupIds]
    );

    const averageScorePayloadKey = useMemo(() => {
        if (!isDepartmentFilter && !selectedGroupIds?.length) return null;
        if (isDepartmentFilter && !selectedDepartmentIds?.length) return null;
        return JSON.stringify(buildFirstTestPayload({ masterCompanyIds }));
    }, [isDepartmentFilter, selectedGroupIds, selectedDepartmentIds, masterCompanyIds]);

    const courseListPayloadKey = useMemo(() => {
        if (!isDepartmentFilter && !selectedGroupIds?.length) return null;
        if (isDepartmentFilter && !selectedDepartmentIds?.length) return null;
        return JSON.stringify(buildFirstTestPayload());
    }, [isDepartmentFilter, selectedGroupIds, selectedDepartmentIds]);

    useEffect(() => {
        if (!averageScorePayloadKey) return;
        fetchDataAverageScore(JSON.parse(averageScorePayloadKey));
    }, [averageScorePayloadKey]);

    useEffect(() => {
        if (!courseListPayloadKey) return;
        fetchDataCourseList(JSON.parse(courseListPayloadKey));
    }, [courseListPayloadKey]);

    const exportCSV = async () => {
        const payload = buildFirstTestPayload();

        const csvData = await flowResult(getDataCSV(payload));
        if (!csvData || csvData.length === 0) {
            console.warn('No data to export.');
            return;
        }

        // Build header
        const headers = [
            '受講者名',
            '法人名',
            '講座名',
            '受講開始日',
            '受講終了日',
            '初回正答率',
            '全受講者初回正答率'
        ];

        // Build each row's content
        const rows = csvData.map((item) => [
            `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            item.companyName || '',
            item.courseName || '',
            item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : '',
            item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : '',
            item.score != null ? +item.score.toFixed(2) + '%' : '',
            item.averageCourseScore ? +item.averageCourseScore.toFixed(2) + '%' : ''
        ]);

        // Combine header and rows into CSV
        const csv = Papa.unparse({
            fields: headers,
            data: rows
        });

        // Encode Shift-JIS
        const sjisEncoded = iconv.encode(csv, 'Shift_JIS');

        // Build file name
        const timestamp = moment().format('YYYYMMDDHHmmss');
        const fileName = `test_result_by_course_${timestamp}.csv`;

        // Create Blob and save
        const blob = new Blob([sjisEncoded], { type: 'text/csv;charset=shift_jis' });
        saveAs(blob, fileName);
    };

    return (
        <div className="container">
            <div className="pd-l-12 pd-r-12 test-first-time-container">
                <div className="chart-container">
                    {userInfoHeader?.groupTwoWaySyncEnable && (
                        <div className="tabs" style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                            <button
                                onClick={() => setActiveTab('average')}
                                style={{
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    backgroundColor: activeTab === 'average' ? '#FFA600' : '#F3F4F6',
                                    color: activeTab === 'average' ? '#fff' : '#333',
                                    boxShadow:
                                        activeTab === 'average'
                                            ? '0 2px 6px rgba(255,166,0,0.4)'
                                            : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Elephamt Elearning
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('ei')
                                    const payload = {
                                        groupId: selectedGroupIds.join(','),
                                        masterCompanyIds
                                    };
                                    fetchDataAverageScore(payload);
                                }}
                                style={{
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    backgroundColor: activeTab === 'ei' ? '#09AA6E' : '#F3F4F6',
                                    color: activeTab === 'ei' ? '#fff' : '#333',
                                    boxShadow:
                                        activeTab === 'ei'
                                            ? '0 2px 6px rgba(9,170,110,0.4)'
                                            : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Elephamt IBT
                            </button>
                        </div>
                    )}
                    <p className="header-title">全受講者の全講座テスト正答率平均値（一回目）</p>

                    <div className="chart-wrapper">
                        {(activeTab === 'average'
                            ? courseList?.elements?.length > 0 &&
                            averageScore != null
                            : eiScore != null
                        ) ? (
                                <DonutChart
                                    percent={activeTab === 'average' ? averageScore : eiScore}
                                    size={200}
                                    thickness={0.8}
                                />
                            ) : (
                                <div className="chart-no-data">
                                    <div>テスト結果なし</div>
                                </div>
                            )}
                    </div>
                </div>
                <div className="list-container">
                    <div className="list-header">
                        <p className="header-title">全受講者の講座ごとのテスト正答率平均値（一回目）</p>
                        <div>
                            <button
                                className="btn-export-csv"
                                onClick={exportCSV}
                                disabled={!courseList?.elements?.length}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 15 15"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ marginRight: '5px' }}
                                >
                                    <path
                                        d="M8.47429 7.03472L9.66175 5.84832C9.86139 5.65521 10.1289 5.54825 10.4066 5.55046C10.6844 5.55268 10.9501 5.6639 11.1467 5.86016C11.3432 6.05642 11.4548 6.32203 11.4574 6.59977C11.46 6.87751 11.3535 7.14517 11.1606 7.34509L8.16391 10.3418C8.06577 10.4405 7.94908 10.5188 7.82055 10.5723C7.69202 10.6258 7.5542 10.6533 7.415 10.6533C7.2758 10.6533 7.13798 10.6258 7.00945 10.5723C6.88092 10.5188 6.76423 10.4405 6.66608 10.3418L3.66937 7.34509C3.57102 7.2466 3.49303 7.1297 3.43986 7.00108C3.38668 6.87245 3.35937 6.73461 3.35947 6.59542C3.35956 6.45624 3.38708 6.31844 3.44043 6.18988C3.49378 6.06133 3.57194 5.94455 3.67042 5.8462C3.76891 5.74785 3.88581 5.66986 4.01444 5.61669C4.14306 5.56352 4.2809 5.5362 4.42009 5.5363C4.55927 5.5364 4.69708 5.56391 4.82563 5.61726C4.95418 5.67062 5.07097 5.74877 5.16931 5.84726L6.35571 7.03472V1.05929C6.35571 0.778346 6.46732 0.508912 6.66597 0.310258C6.86463 0.111603 7.13406 0 7.415 0C7.69594 0 7.96537 0.111603 8.16403 0.310258C8.36268 0.508912 8.47429 0.778346 8.47429 1.05929V7.03472ZM1.05929 12.7114H13.7707C14.0517 12.7114 14.3211 12.823 14.5197 13.0217C14.7184 13.2203 14.83 13.4898 14.83 13.7707C14.83 14.0517 14.7184 14.3211 14.5197 14.5197C14.3211 14.7184 14.0517 14.83 13.7707 14.83H1.05929C0.778346 14.83 0.508912 14.7184 0.310258 14.5197C0.111603 14.3211 0 14.0517 0 13.7707C0 13.4898 0.111603 13.2203 0.310258 13.0217C0.508912 12.823 0.778346 12.7114 1.05929 12.7114Z"
                                        fill="white"
                                    />
                                </svg>
                                CSVダウンロード
                            </button>
                        </div>
                    </div>
                    {courseList?.elements?.length > 0 && (
                        <>
                            <div className="table-container">
                                {courseList?.elements?.map((item, index) => (
                                    <div className={`${index % 2 ? 'bg-gray-1' : 'bg-white'}`} key={index}>
                                        <div className="item-details">
                                            <div className="course-info">
                                                <div className="item-image">
                                                    <img src={item.thumbnail ? item.thumbnail : '/images/img-1.png'} />
                                                </div>
                                                <div className="course-name truncate">
                                                    <p>講座名</p>
                                                    <p
                                                        className="truncate"
                                                        data-bs-toggle="tooltip"
                                                        data-bs-placement="bottom"
                                                        title={item.courseName}
                                                    >
                                                        {item.courseName}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="participants">
                                                <p>受講者数</p>
                                                <p>{item.totalStudents}</p>
                                            </div>
                                            <div
                                                className="correct-rate"
                                                onClick={() => {
                                                    navigate(`/first-test-of-course/${item.courseId}`);
                                                }}
                                            >
                                                <p>テスト正答率平均値（一回目）</p>
                                                {/* <Link to={`/first-test-of-course/${item.courseId}`}> */}
                                                <p className="cursor-pointer">{+item.averageScore.toFixed(2)}%</p>
                                                {/* </Link> */}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* <div className="table-footer">
                            <ReactPaging
                                forcePage={courseList?.pagination?.page - 1 || 0}
                                pageCount={courseList?.pagination?.totalPage || 0}
                                onPageChange={({ selected }) => {
                                    const payload = {
                                        groupId: selectedGroupIds.join(','),
                                        page: selected + 1,
                                    };
                                    fetchData(payload);
                                }}
                            />
                        </div> */}
                        </>
                    )}
                    {courseList?.elements?.length === 0 && <div className="no-data">データはありません</div>}
                </div>
            </div>
        </div>
    );
});

export default FirstTest;
